import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { LIKE } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import Button from "../../framework/Button";
import TextInput from "../../framework/TextInput";
import GlobalContext from "../../framework/GlobalContext";
import { isEmpty } from "../../utils/utils";
import Shortcut from "../../framework/Shortcut";
import { searchDistributor } from "../../services/distributor-services";
import SelectInput from "../../framework/SelectInput";
import NumberInput from "../../framework/NumberInput";

const DistributorReportFilter = ({
  reportRef,
  onFilterShow,
  refreshData = false,
  applyFilterOnMount = true,
}) => {
  const [distributorName, setDistributorName] = useState(null);
  const [address, setaddress] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [gstState, setGstState] = useState("");
  const [bankName, setBankName] = useState("");
  const [orderBy] = useState([{ fieldName: "updatedTime", orderType: "desc" }]);
  const [accountNumber, setAccountNumber] = useState("");
  const apr_filter_distributor_name_ref = useRef();
  const applyFilterRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const [distributorSuggestions, setDistributorSuggestions] = useState([]);
  const withPhoneCap = ({ value }) => value.length <= 10;
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      if (!isEmpty(distributorName)) {
        filters.push(
          createFilterParam("distributorName", distributorName, "string", LIKE)
        );
      }
      if (!isEmpty(address)) {
        filters.push(createFilterParam("address", address, "string", LIKE));
      }
      if (!isEmpty(contact)) {
        filters.push(createFilterParam("contact", contact, "string", LIKE));
      }
      if (!isEmpty(email)) {
        filters.push(createFilterParam("email", email, "string", LIKE));
      }
      if (!isEmpty(gstState)) {
        filters.push(createFilterParam("gstState", gstState, "string", LIKE));
      }
      if (!isEmpty(bankName)) {
        filters.push(createFilterParam("bankName", bankName, "string", LIKE));
      }
      if (!isEmpty(accountNumber)) {
        filters.push(
          createFilterParam("accountNumber", accountNumber, "string", LIKE)
        );
      }

      if (orderBy) {
        result.orderBy = orderBy;
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      reportRef.current?.applyFilters(result);
    },
    [distributorName, address, contact, email, gstState, bankName, accountNumber, orderBy, reportRef]
  );

  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);
  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="distributor_filter_name"
            label="Distributor Name"
            value={distributorName}
            ref={apr_filter_distributor_name_ref}
            freeSolo
            getOptionLabel={(option) => option.distributorName || option}
            isOptionEqualToValue={(option, value) => {
              return option.distributorId === value.distributorId || value;
            }}
            onChange={(e, value) => {
              setDistributorName(value.distributorName);
              setDistributorSuggestions([]);
            }}
            onInputChange={(e) => {
              const inputValue = e.target.value;
              setDistributorName(inputValue);
              if (inputValue && inputValue.length > 2) {
                searchDistributor(`distributorName=${inputValue.trim()}`)
                  .then((resp) => {
                    if (!resp) return;
                    setDistributorSuggestions(resp);
                  })
                  .catch((error) => {
                    console.error("Error fetching distributors:", error);
                  });
              } else {
                setDistributorSuggestions([]);
              }
            }}
            options={distributorSuggestions}
            onFocus={() => setCurrentComponent("distributor_filter_name")}
            markers={JSON.stringify({
              up: "distributor_filter_name",
              down: "distributor_filter_address",
              right: "distributor_filter_address",
              left: "distributor_filter_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="distributor_filter_address"
            label="Address"
            value={address}
            onChange={(e) => setaddress(e.target.value)}
            onFocus={() => setCurrentComponent("distributor_filter_address")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "distributor_filter_name",
                down: "distributor_filter_contact",
                right: "distributor_filter_contact",
                left: "distributor_filter_name",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="distributor_filter_contact"
            label="Contact"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            onFocus={() => setCurrentComponent("distributor_filter_contact")}
            isAllowed={withPhoneCap}
            decimalScale={0}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "distributor_filter_address",
                down: "distributor_filter_email",
                right: "distributor_filter_email",
                left: "distributor_filter_address",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="distributor_filter_email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setCurrentComponent("distributor_filter_email")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "distributor_filter_contact",
                down: "distributor_filter_gst_state",
                right: "distributor_filter_gst_state",
                left: "distributor_filter_contact",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="distributor_filter_gst_state"
            label="GST State"
            value={gstState}
            onChange={(e) => setGstState(e.target.value)}
            onFocus={() => setCurrentComponent("distributor_filter_gst_state")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "distributor_filter_email",
                down: "distributor_filter_bank_name",
                right: "distributor_filter_bank_name",
                left: "distributor_filter_email",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="distributor_filter_bank_name"
            label="Bank Name"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            onFocus={() => setCurrentComponent("distributor_filter_bank_name")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "distributor_filter_gst_state",
                down: "distributor_filter_account_number",
                right: "distributor_filter_account_number",
                left: "distributor_filter_gst_state",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="distributor_filter_account_number"
            label="Account Number"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            onFocus={() =>
              setCurrentComponent("distributor_filter_account_number")
            }
            decimalScale={0}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "distributor_filter_bank_name",
                down: "distributor_filter_apply",
                right: "distributor_filter_apply",
                left: "distributor_filter_bank_name",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  const filterFooter = (
    <FilterGroup>
      <FilterItem>
        <Button
          id="distributor_filter_apply"
          label="Apply"
          ref={applyFilterRef}
          endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
          onClick={() => applyFilterHandler(true)}
          markers={JSON.stringify({
            up: "distributor_filter_account_number",
            down: "distributor_filter_apply",
            right: "distributor_filter_apply",
            left: "distributor_filter_account_number",
          })}
        />
      </FilterItem>
    </FilterGroup>
  );
  useEffect(() => {
    if (refreshData) {
      applyFilterHandler(true);
    }
  }, [refreshData, applyFilterHandler]);
  useEffect(() => {
    if (onFilterShow) {
      apr_filter_distributor_name_ref?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);

  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);

  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default DistributorReportFilter;
